import { FC, useMemo, useState } from 'react';
import { LocalAccountsBoundAdEntityUser } from '../../../../types/LocalAccount';
import { UserRow } from '../../../UsersTable/types';
import { getStatusFromUser } from '../../../UsersTable/UsersTable';
import { BOUND_ACCOUNTS_TABLE_COLUMNS } from '../../consts';
import { Table, Button, ButtonStyles, Icons, EmptyStateProps, EmptyStateIcons } from '@silverfort/sf-components';

export type BoundUserRow = Pick<UserRow, 'id' | 'name' | 'status' | 'domain'>;

export interface BoundAccountsTableProps {
    isLoading: boolean;
    error: unknown;
    boundAccounts: LocalAccountsBoundAdEntityUser[];
    hasAccountMatching: boolean;
}

const ROW_HEIGHT = 48;

const userKeyExtractor = (user: BoundUserRow) => {
    return user.id;
};

const convertUsersToBoundUserRows = (
    users: LocalAccountsBoundAdEntityUser[],
    hasAccountMatching: boolean
): BoundUserRow[] => {
    return users.map((user) => {
        return {
            id: user.guid,
            name: { fullName: user.displayName , userName:hasAccountMatching ? user.userName : undefined },
            status: getStatusFromUser(user),
            domain: user.domainName
        };
    });
};

const BoundUsersTable: FC<BoundAccountsTableProps> = ({ isLoading, error, boundAccounts, hasAccountMatching }) => {
    const [expanded, setExpanded] = useState(true);

    const boundUserRows = useMemo(
        () => convertUsersToBoundUserRows(boundAccounts, hasAccountMatching),
        [boundAccounts, hasAccountMatching]
    );

    const handleToggleExpand = () => {
        setExpanded((prevOpen) => !prevOpen);
    };

    const emptyStateProps: EmptyStateProps = useMemo(() => {
        if (error) {
            return {
                title: 'Error',
                description: (
                    <span>
                        Something went wrong. Try again. If error persists,
                        <br />
                        contact your Silverfort representative.
                    </span>
                ),
                icon: EmptyStateIcons.Error,
                className: 'py-6',
                contentClassName: 'pb-6'
            };
        }

        return {
            title: 'No AD users bound',
            icon: EmptyStateIcons.NoItems,
            contentClassName: '[&_h1]:text-14',
            className: 'py-8'
        };
    }, [error]);

    return (
        <div className="rounded-[8px] border border-sf-gray-100 w-full">
            <div
                className={`px-4 h-14 flex justify-between items-center ${
                    expanded ? 'border-b border-sf-gray-100' : ''
                }`}>
                <span className="font-sf font-medium text-13 leading-[15.87px]">Bound accounts</span>
                <Button
                    iconRight={expanded ? Icons.ChevronUp : Icons.ChevronDown}
                    onClick={handleToggleExpand}
                    buttonStyle={ButtonStyles.TertiarySolid}
                />
            </div>
            <div
                className={`overflow-hidden transition-[max-height] ease-in-out duration-300 ${
                    expanded ? 'max-h-[1000px]' : 'max-h-0'
                }`}>
                <div className="flex p-4">
                    <Table
                        tableContainerClassName="w-full h-fit max-h-[calc(100vh-470px)] overflow-y-auto"
                        columns={BOUND_ACCOUNTS_TABLE_COLUMNS}
                        rows={boundUserRows}
                        keyExtractor={userKeyExtractor}
                        loading={isLoading}
                        EmptyStateProps={emptyStateProps}
                        bodyHeight={boundUserRows.length * ROW_HEIGHT}
                    />
                </div>
            </div>
        </div>
    );
};

export default BoundUsersTable;
